import { PROFILE_KNOWLEDGE_CHAPTERS } from '../profileContract';
import { normalizeSavedKnowledgeItem } from '../knowledgeItems';
import { getKnowledgeItemProfileChapterId } from '../knowledgeItems/careKnowledgeProfileGrouping';
import {
  getCareDocumentOrganizedKnowledgeCards,
  getCareDocumentResolvedGroups,
} from './careDocumentPresentation';
import { timestampToMillis } from './careSourceStatusModel';

const normalizeText = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();

const formatDateLabel = (value = null) => {
  const millis = timestampToMillis(value);
  if (!millis) return '';

  const date = new Date(millis);
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const formatTimeLabel = (value = null) => {
  const millis = timestampToMillis(value);
  if (!millis) return '';

  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(millis));
};

const formatSavedDateLabel = (value = null) => {
  const dateLabel = formatDateLabel(value);
  return dateLabel ? `SAVED ${dateLabel.toUpperCase()}` : '';
};

const hasMeaningfulSourceTitle = (title = '') => {
  const cleanTitle = normalizeText(title);
  if (!cleanTitle) return false;
  if (/^note from\b/i.test(cleanTitle)) return false;
  if (/^care (?:note|source)$/i.test(cleanTitle)) return false;
  return true;
};

const getSourceTimestamp = (source = {}) => (
  source.capturedAt
  || source.createdAt
  || source.updatedAt
  || source.metadata?.careSourceProcessing?.capturedAt
  || source.metadata?.careSourceProcessing?.createdAt
  || source.metadata?.careSourceProcessing?.updatedAt
  || null
);

const getSourceDateLabel = (source = {}) => formatDateLabel(getSourceTimestamp(source));
const getSourceTimeLabel = (source = {}) => formatTimeLabel(getSourceTimestamp(source));

export const getCareLibrarySourceTitle = (source = {}) => {
  const explicitTitle = normalizeText(
    source.metadata?.source?.displayTitle
    || source.sourceMetadata?.displayTitle
    || source.title
    || source.sourceTitle
    || source.careDocumentTitle
    || source.sourceMetadata?.fileName
    || source.metadata?.source?.fileName
    || source.fileName
    || ''
  );

  if (hasMeaningfulSourceTitle(explicitTitle)) {
    return explicitTitle.replace(/\.[a-z0-9]{2,5}$/i, '').trim();
  }

  const timeLabel = getSourceTimeLabel(source);
  if (timeLabel) return `Note · ${timeLabel}`;

  const dateLabel = getSourceDateLabel(source);
  return dateLabel ? `Note · ${dateLabel}` : 'Note';
};

export const getCareLibrarySourceSavedLabel = (source = {}) => getSourceDateLabel(source)
  ? `SAVED ${getSourceDateLabel(source).toUpperCase()}`
  : '';

const getSourceSavedKnowledgeItems = (source = {}) => {
  const savedItems = [
    ...(Array.isArray(source.careKnowledgeSavedItems) ? source.careKnowledgeSavedItems : []),
    ...(Array.isArray(source.metadata?.careSourceProcessing?.careKnowledgeSavedItems)
      ? source.metadata.careSourceProcessing.careKnowledgeSavedItems
      : []),
  ];

  return savedItems;
};

const getCandidateRows = (source = {}) => {
  const savedItems = getSourceSavedKnowledgeItems(source);
  if (savedItems.length) {
    return savedItems.flatMap((item, index) => {
      const title = normalizeText(item.title || '');
      const subtitle = normalizeText(item.summary || item.content || item.sourceText || '');
      if (!title && !subtitle) return [];
      return [{
        id: item.id || item.clientId || `${source.id || 'source'}:${index}`,
        title: title || subtitle,
        subtitle,
        destination: item.destination || 'source',
        sourceId: source.id || '',
        sourceRefs: Array.isArray(item.sourceRefs) ? item.sourceRefs : [],
        needsReview: item.needsReview === true,
        profileChapterId: item.profileChapterId || '',
        knowledgeType: item.knowledgeType || '',
      }];
    });
  }

  const resolvedGroups = getCareDocumentResolvedGroups(source);
  if (resolvedGroups.length) {
    return resolvedGroups.flatMap((group) => {
      const groupRows = [];
      const groupTitle = normalizeText(group.title);
      const isGenericGroupTitle = /^reusable information$/i.test(groupTitle)
        || /^document details$/i.test(groupTitle)
        || /^care notes$/i.test(groupTitle)
        || /^profile updates$/i.test(groupTitle);

      if (groupTitle && !isGenericGroupTitle) {
        groupRows.push({
          id: `${group.id}:title`,
          title: groupTitle,
          kind: 'group',
          sourceId: source.id || '',
        });
      }

      (group.items || []).forEach((item, index) => {
        const title = normalizeText(item.title || item.text || '');
        const subtitle = normalizeText(item.text && item.title && item.text !== item.title ? item.text : item.sourceText || '');
        if (!title && !subtitle) return;
        groupRows.push({
          id: item.id || `${group.id}:${index}`,
          title: title || subtitle,
          subtitle,
          destination: item.destination || 'source',
          sourceId: source.id || '',
          sourceRefs: Array.isArray(item.sourceRefs) ? item.sourceRefs : [],
          needsReview: item.needsReview === true,
          profileChapterId: item.profileChapterId || '',
          knowledgeType: item.knowledgeType || '',
        });
      });

      return groupRows;
    });
  }

  return getCareDocumentOrganizedKnowledgeCards(source).flatMap((card, cardIndex) => {
    const rows = [];
    const cardTitle = normalizeText(card.title);
    const cardBucket = normalizeText(card.bucketLabel);

    if (cardTitle && !/^care notes$/i.test(cardTitle)) {
      rows.push({
        id: `${card.id || `card-${cardIndex}`}:title`,
        title: cardTitle,
        kind: 'card',
        sourceId: source.id || '',
      });
    }

    (card.items || []).forEach((item, index) => {
      const title = normalizeText(item.title || item.text || '');
      const subtitle = normalizeText(item.text && item.title && item.text !== item.title ? item.text : cardBucket || item.sourceText || '');
      if (!title && !subtitle) return;
      rows.push({
        id: item.id || item.sourceItemId || `${card.id || `card-${cardIndex}`}:${index}`,
        title: title || subtitle,
        subtitle,
        destination: item.destination || 'source',
        sourceId: source.id || '',
        sourceRefs: Array.isArray(item.sourceRefs) ? item.sourceRefs : [],
        needsReview: item.needsReview === true,
        profileChapterId: item.profileChapterId || '',
        knowledgeType: item.knowledgeType || '',
      });
    });

    return rows;
  });
};

export const getCareLibrarySourceCollectedRows = (source = {}) => getCandidateRows(source)
  .filter((row) => Boolean(row.title || row.subtitle));

export const getCareLibrarySourceCollectedCount = (source = {}) => getCareLibrarySourceCollectedRows(source).length;

const getItemEvidenceCount = (item = {}) => {
  const refs = Array.isArray(item.sourceRefs) ? item.sourceRefs.filter(Boolean) : [];
  return refs.length || 1;
};

const getItemLastObservedMillis = (item = {}) => {
  const refs = Array.isArray(item.sourceRefs) ? item.sourceRefs.filter(Boolean) : [];
  const refTimes = refs.map((ref) => timestampToMillis(
    ref.confirmedAt
    || ref.createdAt
    || ref.capturedAt
    || ref.timelineDate
    || null
  ));
  const itemTime = timestampToMillis(item.updatedAt || item.capturedAt || item.createdAt || null);
  return Math.max(itemTime, ...refTimes, 0);
};

const formatRelativeDateLabel = (value = 0) => {
  if (!value) return '';
  const date = new Date(value);
  const today = new Date();
  if (
    date.getFullYear() === today.getFullYear()
    && date.getMonth() === today.getMonth()
    && date.getDate() === today.getDate()
  ) {
    return 'Updated today';
  }

  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (
    date.getFullYear() === yesterday.getFullYear()
    && date.getMonth() === yesterday.getMonth()
    && date.getDate() === yesterday.getDate()
  ) {
    return 'Updated yesterday';
  }

  return `Updated ${new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(date)}`;
};

const getSubjectCopy = (label = '') => ({
  [PROFILE_KNOWLEDGE_CHAPTERS[0].label]: 'How the person communicates',
  [PROFILE_KNOWLEDGE_CHAPTERS[1].label]: 'Strategies, preferences & supports',
  [PROFILE_KNOWLEDGE_CHAPTERS[2].label]: 'Patterns and things noticed',
  [PROFILE_KNOWLEDGE_CHAPTERS[3].label]: 'Routines, food, sleep and activities',
  [PROFILE_KNOWLEDGE_CHAPTERS[4].label]: 'Safety information and precautions',
  [PROFILE_KNOWLEDGE_CHAPTERS[5].label]: 'Health history and ongoing care',
}[label] || 'Reusable care knowledge');

const collectLibraryKnowledgeItems = (sources = []) => {
  const itemsByKey = new Map();

  sources.forEach((source) => {
    const savedItems = [];
    if (Array.isArray(source.careKnowledgeSavedItems)) {
      savedItems.push(...source.careKnowledgeSavedItems);
    }
    if (Array.isArray(source.metadata?.careSourceProcessing?.careKnowledgeSavedItems)) {
      savedItems.push(...source.metadata.careSourceProcessing.careKnowledgeSavedItems);
    }

    savedItems.forEach((savedItem, index) => {
      const normalized = normalizeSavedKnowledgeItem({
        ...savedItem,
        childId: savedItem.childId || source.childId || '',
      });
      const chapterId = getKnowledgeItemProfileChapterId(normalized, normalized.profileChapterId || '');
      if (!PROFILE_KNOWLEDGE_CHAPTERS.some((chapter) => chapter.chapterId === chapterId)) return;

      const itemKey = [
        chapterId,
        normalizeText(normalized.title).toLowerCase(),
        normalizeText(normalized.summary).toLowerCase(),
      ].join('|');
      const sourceRefs = Array.isArray(normalized.sourceRefs) ? normalized.sourceRefs : [];
      const evidenceCount = getItemEvidenceCount(normalized);
      const lastObservedAt = getItemLastObservedMillis(normalized) || getItemLastObservedMillis({
        updatedAt: source.updatedAt,
        capturedAt: source.capturedAt || source.createdAt,
        sourceRefs: sourceRefs.length ? sourceRefs : normalized.sourceRefs,
      });

      const existing = itemsByKey.get(itemKey);
      if (existing) {
        const mergedRefs = [...existing.sourceRefs, ...sourceRefs].filter(Boolean);
        itemsByKey.set(itemKey, {
          ...existing,
          sourceRefs: mergedRefs,
          evidenceCount: mergedRefs.length || existing.evidenceCount,
          lastObservedMillis: Math.max(existing.lastObservedMillis, lastObservedAt),
        });
        return;
      }

      itemsByKey.set(itemKey, {
        ...normalized,
        profileChapterId: chapterId,
        sourceId: source.id || normalized.sourceId || '',
        sourceRefs,
        evidenceCount,
        lastObservedMillis: lastObservedAt,
        rowId: normalized.id || `${source.id || 'source'}-${index}`,
      });
    });
  });

  return Array.from(itemsByKey.values())
    .sort((left, right) => right.lastObservedMillis - left.lastObservedMillis);
};

export const buildCareLibrarySubjectGroups = (sources = []) => {
  const items = collectLibraryKnowledgeItems(sources);
  const itemsByChapter = new Map(PROFILE_KNOWLEDGE_CHAPTERS.map((chapter) => [chapter.chapterId, []]));

  items.forEach((item) => {
    if (!itemsByChapter.has(item.profileChapterId)) return;
    itemsByChapter.get(item.profileChapterId).push(item);
  });

  return PROFILE_KNOWLEDGE_CHAPTERS
    .map((chapter) => {
      const chapterItems = itemsByChapter.get(chapter.chapterId) || [];
      if (!chapterItems.length) return null;

      const latestMillis = chapterItems.reduce(
        (max, item) => Math.max(max, item.lastObservedMillis || 0),
        0
      );

      return {
        id: `subject:${chapter.chapterId}`,
        chapterId: chapter.chapterId,
        label: chapter.label,
        description: getSubjectCopy(chapter.label),
        itemCount: chapterItems.length,
        updatedLabel: formatRelativeDateLabel(latestMillis),
        items: chapterItems,
      };
    })
    .filter(Boolean);
};

export const buildCareLibrarySubjectItemDestination = (item = {}) => {
  if (item.destination === 'timeline') {
    const refs = Array.isArray(item.sourceRefs) ? item.sourceRefs.filter(Boolean) : [];
    const timelineRef = refs.find((ref) => ref.timelineEntryId || ref.sourceContext === 'timeline' || ref.sourceCollection === 'dailyLogs');
    return {
      type: 'timeline',
      entryId: timelineRef?.timelineEntryId || item.timelineEntryId || '',
      sourceId: timelineRef?.sourceId || item.sourceId || '',
    };
  }

  if (item.destination === 'source') {
    const refs = Array.isArray(item.sourceRefs) ? item.sourceRefs.filter(Boolean) : [];
    const sourceRef = refs.find((ref) => ref.sourceId);
    return {
      type: 'source',
      sourceId: sourceRef?.sourceId || item.sourceId || '',
    };
  }

  const refs = Array.isArray(item.sourceRefs) ? item.sourceRefs.filter(Boolean) : [];
  const timelineRef = refs.find((ref) => ref.timelineEntryId || ref.sourceContext === 'timeline' || ref.sourceCollection === 'dailyLogs');
  if (timelineRef?.timelineEntryId) {
    return {
      type: 'timeline',
      entryId: timelineRef.timelineEntryId,
      sourceId: timelineRef.sourceId || item.sourceId || '',
    };
  }

  const sourceRef = refs.find((ref) => ref.sourceId);
  if (sourceRef?.sourceId) {
    return {
      type: 'source',
      sourceId: sourceRef.sourceId,
    };
  }

  if (item.sourceId) {
    return {
      type: 'source',
      sourceId: item.sourceId,
    };
  }

  return { type: 'none' };
};

export const formatCareLibraryItemLastObserved = (item = {}) => {
  const millis = item.lastObservedMillis || getItemLastObservedMillis(item);
  if (!millis) return '';
  const date = new Date(millis);
  const today = new Date();
  if (
    date.getFullYear() === today.getFullYear()
    && date.getMonth() === today.getMonth()
    && date.getDate() === today.getDate()
  ) {
    return 'Last observed today';
  }
  return `Last observed ${new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(date)}`;
};

export const formatCareLibraryEvidenceCount = (count = 0) => `Evidence ${count}`;
